import React, { useState, useRef, useEffect } from 'react';

// DenominationChips — controlled chip input for voucher face values.
//
// Renders the current values as pill chips with a × remove button, plus
// a "+ Add" affordance that flips into an inline numeric input. The list
// is kept sorted ascending on every change so the salon doesn't have to
// re-order manually. Duplicates are silently rejected.
//
// Props:
//   values    : number[]  — current denominations
//   currency  : string    — symbol prefix on chips
//   onChange  : (next: number[]) => void

const CloseIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden>
    <path d="M2 2l6 6M8 2l-6 6" />
  </svg>
);

const PlusIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden>
    <path d="M6 2v8M2 6h8" />
  </svg>
);

const DenominationChips = ({ values = [], currency = '£', onChange }) => {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (adding && inputRef.current) inputRef.current.focus();
  }, [adding]);

  const remove = (v) => onChange(values.filter(x => x !== v));

  const commit = () => {
    const n = parseInt(draft, 10);
    setDraft('');
    setAdding(false);
    if (!Number.isFinite(n) || n <= 0) return;
    if (values.includes(n)) return;
    const next = [...values, n].sort((a, b) => a - b);
    onChange(next);
  };

  const onKey = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); commit(); }
    if (e.key === 'Escape') { setDraft(''); setAdding(false); }
  };

  return (
    <div className="v2-ed-chips">
      {values.map((v) => (
        <span key={v} className="v2-ed-chip">
          {currency}{v}
          <button
            type="button"
            className="v2-ed-chip-x"
            onClick={() => remove(v)}
            aria-label={`Remove ${currency}${v}`}
          >
            <CloseIcon />
          </button>
        </span>
      ))}

      {adding ? (
        <span className="v2-ed-chip v2-ed-chip--input">
          <span className="v2-ed-chip-prefix">{currency}</span>
          <input
            ref={inputRef}
            type="number"
            inputMode="numeric"
            min="1"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={onKey}
            placeholder="0"
          />
        </span>
      ) : (
        <button
          type="button"
          className="v2-ed-chip v2-ed-chip--add"
          onClick={() => setAdding(true)}
        >
          <PlusIcon /> Add
        </button>
      )}
    </div>
  );
};

export default DenominationChips;
